/**
 * Absences — a person being away, expressed as capacity rather than constraint.
 *
 * "Jordan is out the week of the 19th" is not a scheduling preference; it is a
 * fact about how many half-days exist. It belongs in availability, which the
 * compiler already turns into forbidden windows per resource, NOT in a DSL term
 * — `do_not_assign_to` has no date field, so expressing an absence that way
 * bans the person for the entire horizon. That happened, and this module is the
 * reason it cannot happen again.
 *
 * Pure: it resolves names to ids, expands a range to working days, and refuses
 * what it cannot make sense of. Writing the rows is the caller's job.
 */
import { dateToBlock, isWorkingDay, parseISODate, toISODate } from "./blocks.js";
import type { AvailabilityMap, Resource } from "./types.js";

/** What the planner is allowed to say. Dates are ISO; the model never gets to invent a format. */
export interface PlannedAbsence {
  resource: string;
  from: string;
  to?: string;
  /** Half-days still available on each day. 0 = fully out; 1 = half a day. */
  halfDays?: number;
  reason?: string;
  /** Cancel an absence rather than record one — "he's back that week after all". */
  clear?: boolean;
}

export interface AbsenceRow {
  resourceId: string;
  day: string;
  availableHalfDays: number;
  reason: string | null;
}

export interface ResolvedAbsence {
  resourceId: string;
  resourceName: string;
  from: string;
  to: string;
  clear: boolean;
  halfDays: number;
  reason: string | null;
  rows: AbsenceRow[];
  summary: string;
}

export interface AbsenceIssue {
  absence: PlannedAbsence;
  message: string;
}

export interface AbsencePlan {
  applied: ResolvedAbsence[];
  issues: AbsenceIssue[];
}

/**
 * What a requirement remembers about an absence it wrote.
 *
 * Availability is global — it outlives the scenario the absence was asked in —
 * so the only thing that makes deleting the requirement a real undo is knowing
 * both what was written and what was there before.
 */
export interface RecordedAbsence {
  resourceId: string;
  resourceName: string;
  from: string;
  to: string;
  clear: boolean;
  halfDays: number;
  reason: string | null;
  summary: string;
  /** Every day this absence touched. */
  days: string[];
  /** Those days' rows exactly as they were beforehand. Days absent from here had none. */
  restore: AbsenceRow[];
}

/** A year of working days. Beyond this, the model has misread the request. */
const MAX_WORKING_DAYS = 260;

const ISO = /^\d{4}-\d{2}-\d{2}$/;

function findResource(ref: string, resources: Resource[]): Resource | undefined {
  const needle = ref.trim().toLowerCase();
  if (!needle) return undefined;
  return (
    resources.find((r) => r.resourceId.toLowerCase() === needle) ??
    resources.find((r) => r.name.trim().toLowerCase() === needle)
  );
}

/**
 * Turn what the planner said into rows the availability table can take.
 *
 * One bad absence does not discard the good ones — the caller reports both, so
 * a request naming two people half-understood still does the half it understood
 * and says which half it did not.
 */
export function planAbsences(
  raw: PlannedAbsence[] | undefined,
  resources: Resource[],
  current?: AvailabilityMap,
): AbsencePlan {
  const applied: ResolvedAbsence[] = [];
  const issues: AbsenceIssue[] = [];

  for (const a of raw ?? []) {
    const person = findResource(String(a?.resource ?? ""), resources);
    if (!person) {
      issues.push({ absence: a, message: `no engineer matches "${a?.resource ?? ""}"` });
      continue;
    }
    const from = String(a.from ?? "");
    const to = String(a.to ?? a.from ?? "");
    if (!ISO.test(from) || !ISO.test(to)) {
      issues.push({ absence: a, message: "an absence needs a YYYY-MM-DD date, not a phrase" });
      continue;
    }
    const start = parseISODate(from);
    const end = parseISODate(to);
    if (end < start) {
      issues.push({ absence: a, message: `absence ends before it starts (${from} → ${to})` });
      continue;
    }

    const rows: AbsenceRow[] = [];
    const halfDays = Math.min(Math.max(Math.round(Number(a.halfDays ?? 0)) || 0, 0), 2);
    const reason = a.reason?.trim() ? a.reason.trim() : "Time off";
    let cursor = start;
    let overrun = false;
    while (cursor <= end) {
      if (isWorkingDay(cursor)) {
        if (rows.length >= MAX_WORKING_DAYS) {
          overrun = true;
          break;
        }
        rows.push({ resourceId: person.resourceId, day: toISODate(cursor), availableHalfDays: halfDays, reason });
      }
      cursor += 86_400_000;
    }
    if (overrun) {
      issues.push({ absence: a, message: `absence is longer than ${MAX_WORKING_DAYS} working days — that is a misread request, not time off` });
      continue;
    }
    if (!rows.length) {
      issues.push({ absence: a, message: `${from} → ${to} contains no working days` });
      continue;
    }

    /**
     * Already recorded, exactly as asked? Then this write does nothing, and the
     * request that produced it almost certainly meant the opposite.
     *
     * The planner turns "Jordan is out that week" into an absence reliably; it
     * turns "Jordan is BACK that week" into the same absence with `clear`
     * unset, which would leave someone marked out who asked to be put back.
     * Re-recording time off that is already on the calendar is the tell, and
     * unlike reading the sentence for the word "back", it cannot misfire on a
     * genuinely new absence.
     */
    const onCalendar = current?.get(person.resourceId);
    if (!a.clear && onCalendar && rows.every((r) => onCalendar.get(r.day) === r.availableHalfDays)) {
      issues.push({
        absence: a,
        message:
          `${person.name} is already marked unavailable for ${from} → ${to}. ` +
          `Nothing was changed — if you meant to put them back, ask to cancel that time off.`,
      });
      continue;
    }

    const verb = a.clear ? "available again" : "unavailable";
    applied.push({
      resourceId: person.resourceId,
      resourceName: person.name,
      from, to,
      clear: Boolean(a.clear),
      halfDays,
      reason,
      rows,
      summary: `${person.name} — ${verb} ${from} → ${to} (${rows.length} working day${rows.length === 1 ? "" : "s"})`,
    });
  }

  return { applied, issues };
}

/** Pair what is about to be written with what is already there. */
export function recordAbsence(a: ResolvedAbsence, prior: AbsenceRow[]): RecordedAbsence {
  const days = a.rows.map((r) => r.day);
  const touched = new Set(days);
  return {
    resourceId: a.resourceId,
    resourceName: a.resourceName,
    from: a.from,
    to: a.to,
    clear: a.clear,
    halfDays: a.halfDays,
    reason: a.reason,
    summary: a.summary,
    days,
    restore: prior.filter((r) => r.resourceId === a.resourceId && touched.has(r.day)),
  };
}

/**
 * Put a recorded absence back into force — when a requirement is re-enabled,
 * or re-applied after being toggled off.
 *
 * Rebuilt from the record rather than re-planned, so a person's calendar cannot
 * drift from what the requirement says it did.
 */
export function applyAbsences(
  recorded: RecordedAbsence[] | null | undefined,
): { upserts: AbsenceRow[]; deletes: { resourceId: string; day: string }[] } {
  const upserts: AbsenceRow[] = [];
  const deletes: { resourceId: string; day: string }[] = [];
  for (const r of recorded ?? []) {
    for (const day of r.days) {
      if (r.clear) deletes.push({ resourceId: r.resourceId, day });
      else upserts.push({ resourceId: r.resourceId, day, availableHalfDays: r.halfDays, reason: r.reason });
    }
  }
  return { upserts, deletes };
}

/**
 * The inverse of writing an absence: put back the rows that existed, and remove
 * the ones this requirement invented. Recording an absence and canceling one
 * undo identically, because both are just "the calendar as it was".
 */
export function undoAbsences(
  recorded: RecordedAbsence[] | null | undefined,
): { upserts: AbsenceRow[]; deletes: { resourceId: string; day: string }[] } {
  const upserts: AbsenceRow[] = [];
  const deletes: { resourceId: string; day: string }[] = [];
  for (const r of recorded ?? []) {
    const restored = new Set(r.restore.map((x) => x.day));
    upserts.push(...r.restore);
    for (const day of r.days) if (!restored.has(day)) deletes.push({ resourceId: r.resourceId, day });
  }
  return { upserts, deletes };
}

// ── reading time off back ─────────────────────────────────────────────

/** One booking, as the person who made it would describe it. */
export interface AbsenceRange {
  resourceId: string;
  from: string;
  to: string;
  /** Working days actually removed. Not the span — a Fri–Mon booking is 2. */
  days: number;
  halfDays: number;
  reason: string | null;
}

/** The next working day after `iso`, skipping Saturday and Sunday. */
function nextWorkingDay(iso: string): string {
  const DAY = 86_400_000;
  let utc = parseISODate(iso) + DAY;
  while (!isWorkingDay(utc)) utc += DAY;
  return toISODate(utc);
}

/**
 * Group stored availability rows back into the bookings they came from.
 *
 * Availability is one row per day because that is what the scheduler consumes,
 * but nobody books a day at a time — and nobody can cancel what they cannot
 * see. Two rows join when they are the same person, the same reason, the same
 * depth of absence, and adjacent as *working* days: booking Friday to Monday
 * stores Friday and Monday and nothing between, so splitting on the calendar
 * gap would show two bookings where one was made.
 *
 * A row leaving a full day available is not time off and is dropped — it is a
 * calendar entry that removes nothing.
 */
export function groupAbsences(rows: AbsenceRow[]): AbsenceRange[] {
  const sorted = rows
    .filter((r) => r.availableHalfDays < 2)
    .slice()
    .sort((a, b) => a.resourceId.localeCompare(b.resourceId) || a.day.localeCompare(b.day));

  const out: AbsenceRange[] = [];
  for (const r of sorted) {
    const open = out[out.length - 1];
    const continues =
      open !== undefined &&
      open.resourceId === r.resourceId &&
      open.halfDays === r.availableHalfDays &&
      (open.reason ?? null) === (r.reason ?? null) &&
      nextWorkingDay(open.to) === r.day;

    if (continues) {
      open.to = r.day;
      open.days += 1;
    } else {
      out.push({
        resourceId: r.resourceId,
        from: r.day,
        to: r.day,
        days: 1,
        halfDays: r.availableHalfDays,
        reason: r.reason ?? null,
      });
    }
  }
  return out;
}

/** A day a person is part or fully out. `halfDays` is how much they are out: 1 a half day, 0 the whole day. */
export interface TimeOffDay {
  resourceId: string;
  day: string;
  /** Half-days still available that day: 0 is out all day, 1 is out half of it. */
  halfDays: 0 | 1;
}

/**
 * The days from `from` onward on which someone is not fully available, for showing time off.
 *
 * Availability is stored per day in half-days (absent = 2, a full day). Only the
 * days short of that are returned, soonest first, so the board carries time off
 * and not a row for every working day.
 */
export function timeOffFrom(availability: Map<string, Map<string, number>>, from: string): TimeOffDay[] {
  const out: TimeOffDay[] = [];
  for (const [resourceId, byDay] of availability) {
    for (const [day, half] of byDay) {
      if (day < from || half >= 2) continue;
      out.push({ resourceId, day, halfDays: half <= 0 ? 0 : 1 });
    }
  }
  return out.sort((a, b) => a.day.localeCompare(b.day) || a.resourceId.localeCompare(b.resourceId));
}

/** A run of consecutive half-day columns on the board, 1-based, as a CSS grid column and span. */
export interface TimeOffSpan { start: number; span: number; halfOnly: boolean }

/**
 * Where one person's time off falls in the visible window, as runs of columns.
 *
 * A column is a half-day block. A full day takes both of its blocks; a half day
 * takes the afternoon, because the morning is the half that stays workable (see
 * isBlockAvailable). Neighbouring days join into one run, so a week off reads as
 * one block rather than five. Days outside the window are clipped, not dropped
 * whole, so time off that began last week still shows on its remaining days.
 * `halfOnly` is true for a run that is only a single half day, for the tooltip.
 */
export function timeOffSpans(days: readonly TimeOffDay[], fromBlock: number, cols: number): TimeOffSpan[] {
  const cells = new Map<number, boolean>(); // column -> is it a half day's afternoon
  for (const t of days) {
    const ms = parseISODate(t.day);
    if (!isWorkingDay(ms)) continue;
    const am = dateToBlock(t.day) - fromBlock + 1;
    const take = t.halfDays === 0 ? [am, am + 1] : [am + 1];
    for (const c of take) if (c >= 1 && c <= cols) cells.set(c, t.halfDays === 1);
  }
  const ordered = [...cells.keys()].sort((a, b) => a - b);
  const out: TimeOffSpan[] = [];
  for (const c of ordered) {
    const last = out[out.length - 1];
    if (last && last.start + last.span === c) {
      last.span++;
      last.halfOnly = false;
    } else {
      out.push({ start: c, span: 1, halfOnly: cells.get(c) === true });
    }
  }
  return out;
}
